import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import posthog from 'posthog-js';
import { EVENTS } from '@lib/analytics';
import { motion, AnimatePresence } from 'framer-motion';
import { TickCircle, Edit2 } from 'iconsax-react';
import { Input, Modal, Skeleton, Tag } from 'antd';
import LndButton from '@components/LndButton';
import { TypingLoader } from '@components/Loader';
import AppLogo, { Theme } from '@components/AppLogo';
import logoCompact from '@assets/logo-compact.svg';
import { useObjectives, useCreateObjective, useDeleteObjective } from '@apis/queries/useObjectives';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@contexts/AuthContext';
import { feedQueryKeys } from '@apis/queries/useFeed';
import { objectiveFeedQueryKeys, useObjectiveFeed } from '@apis/queries/useObjectiveFeed';
import ObjectiveService from '@apis/ObjectiveService';
import type { ObjectiveSuggestion } from '@apis/ObjectiveService';
import CompanyService from '@apis/CompanyService';
import CompanyBrandingService from '@apis/CompanyBrandingService';
import type { ObjectiveProgressDetail } from '@customTypes/objectiveFeed';
import { getBrandCache, clearBrandCache } from '@utils/brandPrefetch';
import ScanOverlay from '@components/ScanOverlay';
import type { ScanInsight } from '@components/ScanOverlay';

// ---------------------------------------------------------------------------
// Constants — intro phases
// ---------------------------------------------------------------------------

/* COMMENTED OUT — replaced by website scan experience, see issue #3136
const INTRO_TEXT =
  "Hi, I'm Leo \u2014 I handle your advertising intelligence, strategy, creative, ad execution, and reporting. Let me research your company so I can tailor everything to your brand.";

const LOADING_PHASES = [
  'Analyzing your website\u2026',
  'Reviewing your brand assets\u2026',
  'Understanding your market position\u2026',
  'Identifying your target audience\u2026',
  'Mapping your competitive landscape\u2026',
];

const TYPEWRITER_CHAR_MS = 60;
*/
const LOGO_PAUSE_MS = 1500;
const POLL_INTERVAL_MS = 4000;
const MAX_WAIT_MS = 60000;
const RESEARCHING_TIMEOUT_MS = 15000;
// ---------------------------------------------------------------------------
// Constants — picker phase
// ---------------------------------------------------------------------------

// Three tailored suggestions from Haiku + one always-present "account review"
// option appended by the /suggestions endpoint = four slots total.
const RECOMMENDED_COUNT = 4;
// Modal is single-select by design: picking one objective lowers cognitive
// load and anchors the audit → kickoff memo flow around a single direction.
// Applies to both onboarding and no_objectives paths.
const MAX_OBJECTIVES = 1;

// ---------------------------------------------------------------------------
// Recommended-card halo
// ---------------------------------------------------------------------------
// Blue drop shadow on the "Recommended" objective card. Intensifies through
// three interaction states:
//   default  → 3px ring @ 18% opacity (soft anchor)
//   hover    → 4px ring @ 26% opacity (intensifying)
//   selected → 4px ring @ 32% opacity (strongest — replaces the hover ring)
// The inner two shadow layers (black @ 4% and @ 6%) provide the ambient
// elevation shared with non-badged cards. Color matches the Ant Design
// Tag (color="blue") rendered at the top of the card.
//
// Tune these three values to tune the feel. A UX designer can iterate here
// without touching component logic.
// ---------------------------------------------------------------------------
const HALO_DEFAULT =
  'shadow-[0_0_0_3px_rgba(24,144,255,0.18),0_0_1px_1px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.04)]';
const HALO_HOVER =
  'hover:shadow-[0_0_0_4px_rgba(24,144,255,0.26),0_0_1px_1px_rgba(0,0,0,0.04),0_2px_6px_rgba(0,0,0,0.06)]';
const HALO_SELECTED =
  'shadow-[0_0_0_4px_rgba(24,144,255,0.32),0_0_1px_1px_rgba(0,0,0,0.04),0_2px_6px_rgba(0,0,0,0.06)]';

// Ambient shadow for non-badged cards. Kept separate so the Recommended
// card's halo replaces (rather than stacks with) this subtle drop shadow.
const CARD_SHADOW_DEFAULT =
  'shadow-[0_0_1px_1px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.04)]';

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IntroPhase = 'loading' | 'scanning' | 'picker';

interface CompanySections {
  [key: string]: { title: string; content: string };
}

interface BrandData {
  description?: string;
  conventions?: string;
  logos?: string[];
}

interface SkillInsight {
  skill_name: string;
  strategy_summary: string;
}

interface SuggestedObjective {
  id: string;
  title: string;
  description: string;
  progressDetail?: ObjectiveProgressDetail;
  discoveryQuestions?: string[];
  kickoffBody?: string;
  skillInsights?: SkillInsight[];
  badge?: string;
}

const FALLBACK_SUGGESTIONS: SuggestedObjective[] = [
  {
    id: 'default-1',
    title: 'Increase Sales & Revenue from Ads',
    description: 'Drive direct product or service sales through advertising campaigns',
  },
  {
    id: 'default-2',
    title: 'Generate More Leads from Ads',
    description: 'Grow lead form submissions, demo requests, and consultation bookings',
  },
  {
    id: 'default-3',
    title: 'Keep Ad Spend Within Budget',
    description: 'Maintain budget discipline and avoid overspending across campaigns',
  },
];

// ---------------------------------------------------------------------------
// Helpers: extract insights client-side from raw data
// ---------------------------------------------------------------------------

function hasBrandContent(assets: BrandData): boolean {
  const desc = assets.description ?? '';
  const conv = assets.conventions ?? '';
  return desc.length > 10 || conv.length > 10;
}

// ---------------------------------------------------------------------------
// Objective picker (right side of modal)
// ---------------------------------------------------------------------------

interface ObjectivePickerProps {
  suggestions: SuggestedObjective[];
  isLoadingSuggestions: boolean;
  selected: SuggestedObjective[];
  onToggle: (objective: SuggestedObjective) => void;
  onAddCustom: (description: string) => void;
  atMax: boolean;
  // When true, at-max cards stay clickable so the single-select swap
  // (handleToggle's onboarding branch) can run. Without this, the disabled
  // attribute would block the click before the swap could happen.
  allowReplace?: boolean;
  isAddingCustom: boolean;
  setIsAddingCustom: (value: boolean) => void;
  hideCustom?: boolean;
}

const ObjectivePicker = ({ suggestions, isLoadingSuggestions, selected, onToggle, onAddCustom, atMax, allowReplace, isAddingCustom, setIsAddingCustom, hideCustom }: ObjectivePickerProps) => {
  const selectedTitles = new Set(selected.map((o) => o.title));
  const [customDescription, setCustomDescription] = useState('');

  const handleAddCustom = () => {
    const trimmed = customDescription.trim();
    if (!trimmed) return;
    onAddCustom(trimmed);
    setCustomDescription('');
    setIsAddingCustom(false);
  };

  const handleCancelCustom = () => {
    setIsAddingCustom(false);
    setCustomDescription('');
  };

  return (
    <div className="flex flex-col gap-2 min-h-0 px-6 relative">

      {/* Scrollable suggestion list */}
      <div
        className="flex flex-col gap-6 p-2 min-h-0 flex-1 overflow-y-auto"
        aria-hidden={isAddingCustom}
        inert={isAddingCustom ? true : undefined}
      >
        {isLoadingSuggestions ? (
          Array.from({ length: RECOMMENDED_COUNT }).map((_, i) => (
            <div
              key={`skeleton-${i}`}
              className="flex-1 rounded-xl border border-transparent p-3 shadow-[0_0_1px_1px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.04)]"
            >
              <Skeleton active paragraph={{ rows: 2, width: ['100%', '60%'] }} title={{ width: '20%' }} />
            </div>
          ))
        ) : (
          suggestions.map((objective) => {
            const isSelected = selectedTitles.has(objective.title);
            const isLocked = atMax && !allowReplace && !isSelected;

            // Pick the shadow for this card. The Recommended card gets the
            // blue halo (see constants at top of file); everything else gets
            // the standard ambient shadow.
            const shadowClass = objective.badge
              ? (isSelected ? HALO_SELECTED : `${HALO_DEFAULT} ${HALO_HOVER}`)
              : CARD_SHADOW_DEFAULT;

            // Border treatment. Selected gets the primary-color outline.
            // Non-badged unselected cards get a primary-color hover border.
            // The Recommended card intentionally has no hover border — the
            // halo alone is the interaction signal, to avoid a blue/primary
            // two-ring clash.
            const borderClass = isSelected
              ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]'
              : isLocked
                ? 'opacity-50 cursor-not-allowed'
                : objective.badge
                  ? ''
                  : 'hover:border-[var(--color-primary)]';

            return (
              <button
                key={objective.id}
                type="button"
                onClick={() => onToggle(objective)}
                disabled={isLocked}
                tabIndex={isAddingCustom ? -1 : 0}
                className={`relative flex items-start gap-3 rounded-xl border border-transparent p-3 text-left transition-all duration-150 cursor-pointer ${shadowClass} ${borderClass}`}
              >
                {objective.badge && (
                  <div className="absolute -top-3 left-2">
                    <Tag color="blue" className="text-xs ml-0 mr-0 rounded-full">
                      {objective.badge}
                    </Tag>
                  </div>
                )}
                <div className={[
                  "shrink-0 flex items-center w-5 h-full justify-center rounded-full transition-colors",
                  isSelected
                    ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                    : 'border-[var(--color-light400)] text-[var(--color-light400)]',
                ].join(' ')}>
                  <TickCircle size={20} variant="Bold" color="currentColor" />
                </div>
                <div className="min-w-0">
                  {objective.description && objective.description !== objective.title ? (
                    <>
                      <p className="text-sm font-medium text-body line-clamp-2">{objective.title}</p>
                      <p className="text-xs text-leo-secondary mt-0.5 leading-relaxed line-clamp-2">
                        {objective.description}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-body line-clamp-2">{objective.title}</p>
                  )}
                </div>
              </button>
            );
          })
        )}

      </div>

      {/* Custom objective button — outside scroll */}
      {!isLoadingSuggestions && !hideCustom && (
        <div className="shrink-0 p-2 text-center">
          <button
            type="button"
            onClick={() => setIsAddingCustom(true)}
            disabled={atMax}
            className={[
              'flex items-center gap-2 p-3 text-left transition-opacity duration-150 w-full justify-center',
              atMax
                ? 'opacity-30 cursor-not-allowed'
                : 'opacity-50 cursor-pointer hover:opacity-100',
            ].join(' ')}
          >
            <Edit2 size={18} color="currentColor" className="text-[var(--color-secondary)]" />
            <span className="text-sm font-medium text-[var(--color-secondary)]">Write your own</span>
          </button>
        </div>
      )}

      {/* Custom objective form — overlays the picker */}
      {isAddingCustom && (
        <div
          className="absolute inset-0 bg-white flex flex-col gap-3 p-6 md:p-8 z-10"
          role="dialog"
          aria-label="Write a custom objective"
        >
          <p className="text-sm font-medium text-body">Custom objective</p>
          <Input.TextArea
            value={customDescription}
            onChange={(e) => setCustomDescription(e.target.value)}
            placeholder="Describe what you want to achieve"
            autoSize={{ minRows: 3, maxRows: 6 }}
            autoFocus
          />
          <div className="flex gap-2 mt-1">
            <LndButton
              variant="link"
              onClick={handleAddCustom}
              disabled={!customDescription.trim()}
            >
              Add
            </LndButton>
            <LndButton
              type="text"
              onClick={handleCancelCustom}
            >
              Cancel
            </LndButton>
          </div>
        </div>
      )}

    </div>
  );
};

// ---------------------------------------------------------------------------
// Leo thinking indicator (shown while suggestions load)
// ---------------------------------------------------------------------------

const LeoThinking = () => (
  <div className="flex flex-col items-center justify-center h-full gap-4">
    <img src={logoCompact} alt="Leo" className="w-[40px] h-[40px]" />
    <div className="flex items-center flex-col gap-2 mb-4">
      <TypingLoader size="medium" />
      <span className="text-sm text-body">Leo is preparing suggestions...</span>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main WelcomeModal
// ---------------------------------------------------------------------------

interface WelcomeModalProps {
  onClose: () => void;
  hideContent?: boolean;
  source?: 'onboarding_redirect' | 'no_objectives';
}

export default function WelcomeModal({ onClose, hideContent, source }: WelcomeModalProps) {
  const { currentUser } = useAuth();
  const companyName = currentUser?.companyName ?? '';
  const isOnboarding = source === 'onboarding_redirect';

  // Phase state machine
  const [phase, setPhase] = useState<IntroPhase>(isOnboarding ? 'scanning' : 'picker');
  const isIntroPhase = phase !== 'picker';

  // --- Intro phase state ---
  const [summaryComplete, setSummaryComplete] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [scanMinDurationReached, setScanMinDurationReached] = useState(false);
  const [scanInsights, setScanInsights] = useState<ScanInsight[]>([]);
  const brandFetchedRef = useRef(false);
  const brandDataRef = useRef<BrandData>({});
  const companySectionsRef = useRef<CompanySections>({});
  const summaryInsightsFiredRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track whether the scan animation is done so we don't
  // jump to the picker before the user has seen it.
  const [introNarrationDone, setIntroNarrationDone] = useState(false);
  const companyUrl = (currentUser as Record<string, unknown>)?.companyUrl as string ?? '';

  // --- Picker phase state ---
  const [selectedObjectives, setSelectedObjectives] = useState<SuggestedObjective[]>([]);
  const [customObjectives, setCustomObjectives] = useState<SuggestedObjective[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestedObjective[]>(FALLBACK_SUGGESTIONS);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const { objectiveGroups: feedGroups } = useObjectiveFeed();
  const { objectives: existingObjectives, isLoading: isLoadingObjectives } = useObjectives();
  const createObjective = useCreateObjective();
  const deleteObjective = useDeleteObjective();
  const queryClient = useQueryClient();
  const hasSeeded = useRef(false);
  const hasFetchedSuggestions = useRef(false);
  const hasTrackedViewRef = useRef(false);
  const hasSavedRef = useRef(false);

  // =========================================================================
  // Intro phase effects
  // =========================================================================

  // Hydrate from prefetch cache — brand assets may have been pre-fetched
  // by the onboarding drawer while the user completed remaining steps.
  useEffect(() => {
    const cached = getBrandCache();
    if (!cached) return;
    if (cached.screenshotUrl && !screenshotUrl) {
      setScreenshotUrl(cached.screenshotUrl);
    }
    if (cached.logos.length > 0 && !logoUrl) {
      setLogoUrl(cached.logos[0]);
    }
    if (cached.description || cached.conventions) {
      brandDataRef.current = {
        description: cached.description,
        conventions: cached.conventions,
        logos: cached.logos,
      };
      brandFetchedRef.current = true;
      // Fire lightweight LLM call for brand insights from cache
      const brandText = [cached.description || '', cached.conventions || ''].filter(Boolean).join('\n\n');
      if (brandText.length > 20) {
        ObjectiveService.getScanInsights(brandText, 'brand').then((insights) => {
          if (insights.length > 0) {
            setScanInsights((prev) => [
              ...prev,
              ...insights.map((label) => ({ label, category: 'Brand' })),
            ]);
          }
        });
      }
    }
    clearBrandCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Logo appears, pause, then start scanning
  useEffect(() => {
    if (phase !== 'loading') return;
    const timer = setTimeout(() => setPhase('scanning'), LOGO_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // Scan minimum duration callback
  const handleScanMinDuration = useCallback(() => {
    setScanMinDurationReached(true);
  }, []);

  // When scan min duration reached, mark intro narration done so we can
  // transition to picker once suggestions are loaded.
  useEffect(() => {
    if (phase !== 'scanning') return;
    if (!scanMinDurationReached) return;
    setIntroNarrationDone(true);
  }, [phase, scanMinDurationReached]);

  // Poll for branding + company summary — start during loading so logo/screenshot
  // are ready by the time scanning begins (avoids showing WAF error pages in iframe).
  useEffect(() => {
    if (phase !== 'loading' && phase !== 'scanning') return;

    const poll = async () => {
      // Poll brand assets (also captures screenshot_url for scanning phase)
      if (!brandFetchedRef.current && companyName) {
        try {
          const assets = await CompanyBrandingService.getBrandAssets(companyName);
          // Capture screenshot URL for scan overlay fallback
          if (assets.screenshot_url && !screenshotUrl) {
            setScreenshotUrl(assets.screenshot_url);
          }
          // Capture first logo for logo fallback
          if (assets.logos?.length > 0 && !logoUrl) {
            setLogoUrl(assets.logos[0]);
          }
          if (hasBrandContent(assets)) {
            brandFetchedRef.current = true;
            brandDataRef.current = assets;
            // Fire lightweight LLM call to extract brand insights
            const brandText = [
              assets.description || '',
              assets.conventions || '',
            ].filter(Boolean).join('\n\n');
            if (brandText.length > 20) {
              ObjectiveService.getScanInsights(brandText, 'brand').then((insights) => {
                if (insights.length > 0) {
                  setScanInsights((prev) => [
                    ...prev,
                    ...insights.map((label) => ({ label, category: 'Brand' })),
                  ]);
                }
              });
            }
          }
        } catch {
          // Will retry
        }
      }

      // Poll company summary
      if (!summaryComplete) {
        try {
          const data = await CompanyService.getCompanyContext();
          if (data?.company_summary_json && typeof data.company_summary_json !== 'string') {
            const sections = data.company_summary_json as CompanySections;
            const contentKeys = Object.keys(sections).filter((k) => !k.startsWith('_'));
            const hasContent = contentKeys.some(
              (k) => sections[k].content && sections[k].content.length > 20,
            );
            if (hasContent) {
              companySectionsRef.current = sections;
              // Fire lightweight LLM call to extract summary insights
              const summaryText = contentKeys
                .map((k) => sections[k].content)
                .filter((c) => c && c.length > 20)
                .join('\n\n');
              if (summaryText.length > 20 && !summaryInsightsFiredRef.current) {
                summaryInsightsFiredRef.current = true;
                ObjectiveService.getScanInsights(summaryText, 'summary').then((insights) => {
                  if (insights.length > 0) {
                    const categories = ['Overview', 'Products', 'Audience', 'Positioning', 'Industry'];
                    setScanInsights((prev) => [
                      ...prev,
                      ...insights.map((label, i) => ({
                        label,
                        category: categories[i % categories.length],
                      })),
                    ]);
                  }
                });
              }

              const partial = contentKeys.length < 4;
              if (!partial) {
                setSummaryComplete(true);
                if (pollRef.current) clearInterval(pollRef.current);
              }
            }
          }
        } catch {
          // Will retry
        }
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [phase, companyName, summaryComplete, screenshotUrl]);

  // If stuck in scanning phase (no data found), advance after a timeout
  // so the user isn't waiting forever on the scan animation.
  useEffect(() => {
    if (phase !== 'scanning' || !isOnboarding) return;
    const timer = setTimeout(() => {
      setIntroNarrationDone(true);
    }, RESEARCHING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [phase, isOnboarding]);

  // Keep polling for company summary after visual phases end.
  // The phase-based poll stops when we leave researching/narrating, but
  // suggestion fetching depends on summaryComplete being true.
  useEffect(() => {
    if (!isOnboarding || summaryComplete) return;
    const poll = async () => {
      try {
        const data = await CompanyService.getCompanyContext();
        if (data?.company_summary_json && typeof data.company_summary_json !== 'string') {
          const sections = data.company_summary_json as CompanySections;
          const contentKeys = Object.keys(sections).filter((k) => !k.startsWith('_'));
          const hasContent = contentKeys.some(
            (k) => sections[k].content && sections[k].content.length > 20,
          );
          if (hasContent && contentKeys.length >= 4) {
            setSummaryComplete(true);
          }
        }
      } catch {
        // Will retry
      }
    };
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOnboarding, summaryComplete]);

  // Timeout fallback — force both summaryComplete and introNarrationDone
  // after MAX_WAIT_MS so we always reach the picker eventually.
  useEffect(() => {
    if (!isOnboarding) return;
    const timer = setTimeout(() => {
      setSummaryComplete(true);
      setIntroNarrationDone(true);
    }, MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isOnboarding]);

  // =========================================================================
  // Suggestion fetching
  // =========================================================================

  // Track modal view (fire once)
  useEffect(() => {
    if (hasTrackedViewRef.current || isLoadingObjectives) return;
    hasTrackedViewRef.current = true;
    posthog.capture(EVENTS.OBJECTIVES.MODAL_VIEWED, {
      source: source ?? 'no_objectives',
      existing_objective_count: existingObjectives.length,
    });
  }, [isLoadingObjectives, existingObjectives.length, source]);

  // Track modal dismiss on unmount (if not saved)
  useEffect(() => {
    return () => {
      if (hasSavedRef.current) return;
      posthog.capture(EVENTS.OBJECTIVES.MODAL_DISMISSED, {
        objectives_selected_at_dismiss: selectedObjectives.length,
        had_suggestions: suggestions.length > 0,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch AI-generated suggestions.
  // For onboarding: wait until both introNarrationDone AND summaryComplete so
  // the backend has a real company summary to generate tailored suggestions.
  // For no_objectives: fetch on mount when objectives are loaded.
  useEffect(() => {
    if (hasFetchedSuggestions.current || isLoadingObjectives) return;
    if (isOnboarding && (!introNarrationDone || !summaryComplete)) return;

    hasFetchedSuggestions.current = true;

    const slotsForSuggestions = Math.max(0, RECOMMENDED_COUNT - existingObjectives.length);

    if (slotsForSuggestions === 0) {
      setSuggestions([]);
      setIsLoadingSuggestions(false);
      if (!isOnboarding) setPhase('picker');
      posthog.capture(EVENTS.OBJECTIVES.SUGGESTIONS_LOADED, {
        suggestion_count: 0,
        used_fallback: false,
        slots_available: 0,
      });
      return;
    }

    let retried = false;

    const applyResults = (result: ObjectiveSuggestion[]) => {
      if (result.length > 0) {
        const existingTitles = new Set(existingObjectives.map((o) => o.title));
        const mapped = result
          .filter((s) => !existingTitles.has(s.title))
          .slice(0, slotsForSuggestions)
          .map((s, i) => ({
            id: `suggestion-${i}`,
            title: s.title,
            description: s.description,
            progressDetail: s.progress_detail,
            discoveryQuestions: s.discovery_questions,
            kickoffBody: s.kickoff_body,
            skillInsights: s.skill_insights,
            badge: s.badge,
          }));
        setSuggestions(mapped);
        setIsLoadingSuggestions(false);
        posthog.capture(EVENTS.OBJECTIVES.SUGGESTIONS_LOADED, {
          suggestion_count: mapped.length,
          used_fallback: false,
          slots_available: slotsForSuggestions,
        });
      } else if (isOnboarding && !retried) {
        // Company summary may not be ready yet — retry once after a delay.
        retried = true;
        setTimeout(() => {
          ObjectiveService.getSuggestions(slotsForSuggestions)
            .then(applyResults)
            .catch(handleError);
        }, POLL_INTERVAL_MS * 2);
      } else {
        setIsLoadingSuggestions(false);
        posthog.capture(EVENTS.OBJECTIVES.SUGGESTIONS_LOADED, {
          suggestion_count: FALLBACK_SUGGESTIONS.length,
          used_fallback: true,
          slots_available: slotsForSuggestions,
        });
      }
      if (!isOnboarding) setPhase('picker');
    };

    const handleError = (err: unknown) => {
      console.error('Failed to fetch objective suggestions:', err);
      setIsLoadingSuggestions(false);
      posthog.capture(EVENTS.OBJECTIVES.SUGGESTIONS_LOADED, {
        suggestion_count: FALLBACK_SUGGESTIONS.length,
        used_fallback: true,
        slots_available: slotsForSuggestions,
      });
      if (!isOnboarding) setPhase('picker');
    };

    ObjectiveService.getSuggestions(slotsForSuggestions)
      .then(applyResults)
      .catch(handleError);
  }, [isLoadingObjectives, existingObjectives, isOnboarding, introNarrationDone, summaryComplete]);

  // For onboarding: transition to picker only once BOTH the intro narration
  // has finished AND suggestions have loaded. This prevents jumping to the
  // picker before the user has seen the full Leo intro animation.
  useEffect(() => {
    if (!isOnboarding || phase === 'picker') return;
    if (!introNarrationDone || isLoadingSuggestions) return;
    setPhase('picker');
  }, [isOnboarding, phase, introNarrationDone, isLoadingSuggestions]);

  // Seed selected objectives from existing data
  useEffect(() => {
    if (hasSeeded.current || isLoadingObjectives || isLoadingSuggestions) return;
    hasSeeded.current = true;

    if (existingObjectives.length > 0) {
      const suggestionsByTitle = new Map(suggestions.map((d) => [d.title, d]));
      const feedDetailByTitle = new Map(feedGroups.map((g) => [g.objective.title, g.progressDetail]));
      const seeded = existingObjectives.map((obj) => {
        const matchingSuggestion = suggestionsByTitle.get(obj.title);
        if (matchingSuggestion) return matchingSuggestion;
        return { id: obj.id, title: obj.title, description: obj.description ?? '', progressDetail: feedDetailByTitle.get(obj.title) };
      });
      setSelectedObjectives(seeded);
    } else if (suggestions.length > 0) {
      // Auto-select the "Recommended" (badged) option so overwhelmed users
      // can click straight through. Applies to both onboarding and
      // no_objectives paths. Falls back to the first suggestion if none
      // carries a badge (e.g. fallback-only responses).
      const recommended = suggestions.find((s) => !!s.badge) ?? suggestions[0];
      setSelectedObjectives([recommended]);
    }
  }, [existingObjectives, isLoadingObjectives, isLoadingSuggestions, suggestions, feedGroups]);

  const progressDetailByTitle = useMemo(() => {
    const map = new Map<string, ObjectiveProgressDetail>();
    feedGroups.forEach((g) => { if (g.progressDetail) map.set(g.objective.title, g.progressDetail); });
    suggestions.forEach((s) => { if (s.progressDetail) map.set(s.title, s.progressDetail); });
    return map;
  }, [feedGroups, suggestions]);

  const pickerItems = useMemo(() => {
    const existingAsSuggested: SuggestedObjective[] = existingObjectives.map((obj) => ({
      id: obj.id,
      title: obj.title,
      description: obj.description ?? '',
      progressDetail: progressDetailByTitle.get(obj.title),
    }));
    const existingTitles = new Set(existingObjectives.map((o) => o.title));
    const filteredSuggestions = suggestions.filter((s) => !existingTitles.has(s.title));
    return [...existingAsSuggested, ...filteredSuggestions, ...customObjectives];
  }, [existingObjectives, suggestions, progressDetailByTitle, customObjectives]);

  // =========================================================================
  // Picker handlers
  // =========================================================================

  // Single-select across both onboarding and no_objectives flows: clicking
  // a selected card deselects it, clicking another card swaps to it.
  const handleToggle = useCallback((objective: SuggestedObjective) => {
    setSelectedObjectives((prev) => {
      const exists = prev.find((o) => o.title === objective.title);
      return exists ? [] : [objective];
    });
  }, []);

  const handleAddCustom = useCallback((description: string) => {
    const customObj: SuggestedObjective = {
      id: `custom-${Date.now()}`,
      title: description,
      description,
    };
    setCustomObjectives((prev) => [...prev, customObj]);
    // Auto-select only the custom objective (deselect others)
    setSelectedObjectives([customObj]);
    posthog.capture(EVENTS.OBJECTIVES.CUSTOM_ADDED, {
      custom_description_length: description.length,
    });
  }, []);

  const canClose = selectedObjectives.length > 0;
  const atMax = selectedObjectives.length >= MAX_OBJECTIVES;

  const handleClose = useCallback(async () => {
    if (selectedObjectives.length === 0) return;

    const existingTitles = new Set(existingObjectives.map((o) => o.title));
    const selectedTitles = new Set(selectedObjectives.map((o) => o.title));
    const newObjectives = selectedObjectives.filter((obj) => !existingTitles.has(obj.title));
    const removedObjectives = existingObjectives.filter((obj) => !selectedTitles.has(obj.title));

    setIsSaving(true);
    try {
      const [createResults] = await Promise.all([
        Promise.all(
          newObjectives.map((obj) =>
            createObjective.mutateAsync({
              title: obj.title,
              description: obj.description,
              progress_detail: obj.progressDetail,
              discovery_questions: obj.discoveryQuestions,
              kickoff_body: obj.kickoffBody,
              skill_insights: obj.skillInsights,
            }),
          ),
        ),
        Promise.all(
          removedObjectives.map((obj) =>
            deleteObjective.mutateAsync(obj.id),
          ),
        ),
      ]);

      const idMap = new Map<string, string>();
      existingObjectives
        .filter((obj) => selectedTitles.has(obj.title))
        .forEach((obj) => idMap.set(obj.title, obj.id));
      createResults.forEach((result, i) => {
        if (result?.id) idMap.set(newObjectives[i].title, result.id);
      });

      await ObjectiveService.classifyPosts();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: feedQueryKeys.all }),
        queryClient.invalidateQueries({ queryKey: objectiveFeedQueryKeys.all }),
      ]);

      hasSavedRef.current = true;

      const suggestionTitles = new Set(suggestions.map((s) => s.title));
      const suggestedCount = selectedObjectives.filter((o) => suggestionTitles.has(o.title)).length;
      const customCount = selectedObjectives.filter((o) => o.id.startsWith('custom-')).length;
      const existingKept = selectedObjectives.filter((o) => existingTitles.has(o.title)).length;

      posthog.capture(EVENTS.OBJECTIVES.SAVED, {
        total_objectives: selectedObjectives.length,
        suggested_objectives: suggestedCount,
        custom_objectives: customCount,
        existing_objectives_kept: existingKept,
        objectives_removed: removedObjectives.length,
        suggestion_titles: selectedObjectives.map((o) => o.title),
      });
    } catch (err) {
      console.error('Failed to save/classify:', err);
    }

    setIsSaving(false);

    // Never auto-open chat after objective creation. The feed surfaces the
    // next steps (structured memos + birth post + kickoff update) which are
    // user-initiated — a user clicks the memo they care about and Leo starts
    // the conversation they chose. Auto-opening chat here would take agency
    // back at the exact moment the user just expressed it.

    await new Promise((r) => requestAnimationFrame(r));
    onClose();
  }, [selectedObjectives, existingObjectives, createObjective, deleteObjective, queryClient, onClose, suggestions]);

  // =========================================================================
  // Render
  // =========================================================================

  const modalStyles = useMemo(() =>
    isIntroPhase
      ? {
        body: { padding: 0 },
        content: { background: 'transparent', borderRadius: 0, boxShadow: 'none', padding: 0 },
        root: {
          background: 'url(/images/clouds.png) center / cover no-repeat',
          padding: 0,
        },
        container: {
          padding: 0,
          borderRadius: 12,
          overflow: 'hidden',
        }
      }
      : {
        container: { padding: 0, borderRadius: 12, overflow: 'hidden' },
        body: { padding: 0 },
      }
    , [isIntroPhase]);

  return (
    <Modal
      open
      closable={false}
      footer={null}
      width={isIntroPhase ? 720 : 600}
      centered
      maskClosable={false}
      styles={modalStyles}
      focusable={{ trap: true }}
      rootClassName="welcome-modal"
    >
      <AnimatePresence mode="wait">
        {isIntroPhase ? (
          /* ----- Intro content (loading → scanning) ----- */
          <motion.div
            key="intro-content"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="flex flex-col items-center"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                minHeight: 380,
              }}
            >
              <div className="flex flex-col items-center gap-6 w-full">
                {/* Loading phase — just the logo */}
                {phase === 'loading' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <AppLogo compact size={48} displayLeoLogo theme={Theme.DARK} />
                  </motion.div>
                )}

                {/* Scanning phase — website scan experience */}
                {phase === 'scanning' && (
                  <ScanOverlay
                    companyUrl={companyUrl}
                    companyName={companyName}
                    screenshotUrl={screenshotUrl}
                    logoUrl={logoUrl}
                    insights={scanInsights}
                    onMinDurationReached={handleScanMinDuration}
                  />
                )}
              </div>
            </div>
          </motion.div>
        ) : (
          /* ----- Picker content ----- */
          <motion.div
            key="picker-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="relative w-full h-full flex flex-col">
              {hideContent && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <TypingLoader size="large" />
                </div>
              )}
              <div
                className={`flex-1 flex flex-row transition-opacity duration-300 ${hideContent ? ' opacity-0' : ''}`}
              >
                <div className="flex min-w-0 flex-1 flex-col border-l border-[var(--color-light200)] bg-white pb-8 relative overflow-hidden rounded-2xl">
                  <div className="flex flex-1 overflow-y-auto flex-col justify-between min-h-0">
                    <div className="flex gap-3 flex-col mb-4">
                      <div
                        className="relative overflow-hidden rounded-t-2xl px-10 pt-8 pb-4 flex flex-col gap-1"
                      >
                        <h2 className="relative z-10 text-xl font-semibold text-body shrink-0">
                          What should we focus on first?
                        </h2>
                        <p className="relative z-10 text-sm text-leo-secondary leading-relaxed shrink-0">
                          Pick one objective to start with, you can add more later.
                        </p>
                      </div>
                    </div>

                    {isLoadingSuggestions ? (
                      <div className="flex-1 flex items-center justify-center">
                        <LeoThinking />
                      </div>
                    ) : (
                      <ObjectivePicker
                        suggestions={pickerItems}
                        isLoadingSuggestions={isLoadingSuggestions}
                        selected={selectedObjectives}
                        onToggle={handleToggle}
                        onAddCustom={handleAddCustom}
                        atMax={atMax}
                        // Modal is single-select — swap on click instead of
                        // disabling other cards at max.
                        allowReplace
                        isAddingCustom={isAddingCustom}
                        setIsAddingCustom={setIsAddingCustom}
                        hideCustom={false}
                      />
                    )}

                    <div className={`flex items-center justify-between px-8${isAddingCustom ? ' invisible' : ''}`}>
                      {/* Single-select — no "Maximum of N" message needed. */}
                      <div className="flex-grow" />
                      <LndButton
                        variant="primary"
                        size="large"
                        onClick={handleClose}
                        disabled={!canClose || isSaving}
                        data-attr-welcome-modal-continue
                        className="mb-2 mt-6"
                      >
                        {isSaving ? 'Leo is reviewing your goals...' : "Let's get to work"}
                      </LndButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
}
